import axios from "axios";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Loader2,
  User,
  Hash,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  Search,
} from "lucide-react";

import { Button } from "../Components/Global/UI/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../Components/Global/UI/Dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Components/Global/UI/table";
import { Badge } from "../Components/Global/UI/Badge";
import { Eye } from "lucide-react";
import { BASE_URL } from "./Settings/DASHBOARD_SETTINGS";
import { Input } from "../Components/Global/UI/Input";

const StaffList = () => {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [employeeGroups, setEmployeeGroups] = useState([
    { id: 23046, name: "ACA" },
    { id: 23054, name: "ACA Driver" },
    { id: 23407, name: "ACA Driver B1" },
    { id: 23984, name: "ACA EAC" },
    { id: 23053, name: "Dispatch" },
    { id: 17779, name: "EMT" },
    { id: 17780, name: "JACE Medical" },
    { id: 17781, name: "Management" },
    { id: 17782, name: "Marketing" },
    { id: 14676, name: "Nurse" },
    { id: 23048, name: "Paramedic" },
    { id: 24173, name: "PHECC Advanced Paramedic" },
    { id: 24182, name: "PHECC Advanced Paramedic" },
    { id: 24171, name: "PHECC EMT " },
    { id: 24172, name: "PHECC Paramedic" },
    { id: 23387, name: "Supervisor" },
    { id: 23055, name: "Teach / Trainer" },
  ]);

  useEffect(() => {
    fetchStaff();
    // fetchEmployeeGroups(); // Temporarily disabled to use static data
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/staff`);
      if (response.data.success) {
        setStaffList(response.data.staff);
      } else {
        toast.error(response.data.error || "Failed to fetch staff.");
      }
    } catch (error) {
      toast.error("Error fetching staff.");
      console.error("Error fetching staff:", error);
    }
  };

  const fetchEmployeeGroups = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/planday/employee-groups`);
      if (response.data.success) {
        setEmployeeGroups(response.data.data || []);
      } else {
        console.error("Failed to fetch employee groups.");
      }
    } catch (error) {
      console.error("Error fetching employee groups:", error);
    }
  };

  const syncPlanday = async () => {
    setIsSyncing(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/planday/sync-staff`);
      if (response.data.success) {
        const { matched, updated } = response.data;
        toast.success(
          `Synced with Planday successfully! ${matched} matched, ${updated} updated.`
        );
        fetchStaff();
      } else {
        toast.error(response.data.message || "Failed to sync with Planday.");
      }
    } catch (error) {
      toast.error("Error syncing with Planday.");
      console.error("Error syncing with Planday:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getGroupName = (id) => {
    if (!Array.isArray(employeeGroups)) return `Group ${id}`;
    const group = employeeGroups.find(g => g.id === Number(id));
    return group ? group.name : `Group ${id}`;
  };

  const getGroupStyle = (name) => {
    const groupColors = {
      'ACA': { bg: '#FFB3BA', text: '#8B0000' }, // Light red
      'ACA Driver': { bg: '#FFDFBA', text: '#8B4513' }, // Light orange
      'ACA Driver B1': { bg: '#FFFFBA', text: '#8B8B00' }, // Light yellow
      'ACA EAC': { bg: '#BAFFBA', text: '#006400' }, // Light green
      'Dispatch': { bg: '#BAE1FF', text: '#00008B' }, // Light blue
      'EMT': { bg: '#D1BAFF', text: '#4B0082' }, // Light purple
      'JACE Medical': { bg: '#FFBAE1', text: '#8B008B' }, // Light pink
      'Management': { bg: '#E6E6FA', text: '#4B0082' }, // Light lavender
      'Marketing': { bg: '#F0E68C', text: '#8B8B00' }, // Light khaki
      'Nurse': { bg: '#FFA07A', text: '#8B0000' }, // Light salmon
      'Paramedic': { bg: '#FFDAB9', text: '#8B4513' }, // Peach puff
      'PHECC Advanced Paramedic': { bg: '#87CEEB', text: '#00008B' }, // Sky blue
      'PHECC EMT': { bg: '#DDA0DD', text: '#8B008B' }, // Plum
      'PHECC Paramedic': { bg: '#F5DEB3', text: '#8B4513' }, // Wheat
      'Supervisor': { bg: '#FFE4E1', text: '#8B0000' }, // Misty rose
      'Teach / Trainer': { bg: '#E0FFFF', text: '#00008B' }, // Light cyan
    };
    return groupColors[name] || { bg: '#FFFACD', text: '#8B8B00' }; // Default lemon chiffon
  };

  // Filter staff based on search term
  const filteredStaff = staffList.filter(
    (staff) =>
      staff.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const activeStaff = filteredStaff.filter(
    (staff) => staff.isActive !== false
  ).length;
  const inactiveStaff = filteredStaff.length - activeStaff;

  return (
    <div>
      <div className="max-w-7xl mx-auto py-10">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Staff Management
            </h1>
            <p className="text-gray-600 text-sm">
              Manage your team members and sync with Planday
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={syncPlanday}
              disabled={isSyncing}
              className="flex items-center gap-2"
            >
              {isSyncing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {isSyncing ? "Syncing" : "Sync Planday"}
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-6 mb-6 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="font-medium">{staffList.length}</span>
            <span className="text-gray-500">Total Staff</span>
          </div>
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-600">{activeStaff}</span>
            <span className="text-gray-500">Active</span>
          </div>
          <div className="flex items-center gap-2">
            <UserX className="w-4 h-4 text-red-600" />
            <span className="font-medium text-red-600">{inactiveStaff}</span>
            <span className="text-gray-500">Inactive</span>
          </div>
        </div>

        {/* Search */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search staff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Staff Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-48">Name</TableHead>
                <TableHead className="w-32">Contact</TableHead>
                <TableHead className="w-40">Groups</TableHead>
                <TableHead className="w-24">Hire Date</TableHead>
                <TableHead className="w-20">Status</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStaff.map((staff) => (
                <TableRow key={staff._id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{staff.full_name}</div>
                      <div className="text-sm text-gray-500">
                        ID: {staff.plandayID || "Not synced"}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {staff.email && <div>{staff.email}</div>}
                      {staff.phone && <div>{staff.phone}</div>}
                      {staff.cellPhone && <div>Cell: {staff.cellPhone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {staff.employeeGroups && staff.employeeGroups.length > 0 ? (
                        staff.employeeGroups.map((groupId) => {
                          const groupName = getGroupName(groupId);
                          const style = getGroupStyle(groupName);
                          return (
                            <Badge key={groupId} style={{ backgroundColor: style.bg, color: style.text, border: 'none' }}>
                              {groupName}
                            </Badge>
                          );
                        })
                      ) : (
                        "N/A"
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {staff.hireDate ? new Date(staff.hireDate).toLocaleDateString() : "N/A"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      style={
                        staff.isActive !== false
                          ? { backgroundColor: '#4CAF50', color: 'white', border: 'none' } // Green for active
                          : { backgroundColor: '#F44336', color: 'white', border: 'none' } // Red for inactive
                      }
                    >
                      {staff.isActive !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedStaff(staff);
                        setShowViewDialog(true);
                      }}
                      className="hover:bg-gray-100"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {staffList.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No staff members yet.
            </div>
          )}
        </div>

        {/* View Staff Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Staff Details</DialogTitle>
              <DialogDescription>
                Detailed information for {selectedStaff?.full_name}
              </DialogDescription>
            </DialogHeader>
            {selectedStaff && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      First Name
                    </label>
                    <Input readOnly value={selectedStaff.first_name} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      Last Name
                    </label>
                    <Input readOnly value={selectedStaff.last_name} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      Full Name
                    </label>
                    <Input readOnly value={selectedStaff.full_name || "N/A"} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Hash className="h-4 w-4" />
                      Planday ID
                    </label>
                    <Input readOnly value={selectedStaff.plandayID || "N/A"} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <Input readOnly value={selectedStaff.email || "N/A"} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Phone className="h-4 w-4" />
                      Phone
                    </label>
                    <Input readOnly value={selectedStaff.phone || "N/A"} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Phone className="h-4 w-4" />
                      Cell Phone
                    </label>
                    <Input readOnly value={selectedStaff.cellPhone || "N/A"} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      Username
                    </label>
                    <Input readOnly value={selectedStaff.userName || "N/A"} />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Calendar className="h-4 w-4" />
                      Date Created
                    </label>
                    <Input
                      readOnly
                      value={
                        selectedStaff.dateTimeCreated
                          ? new Date(selectedStaff.dateTimeCreated).toLocaleDateString()
                          : "N/A"
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <RefreshCw className="h-4 w-4" />
                      Last Sync
                    </label>
                    <Input
                      readOnly
                      value={
                        selectedStaff.lastSync
                          ? new Date(selectedStaff.lastSync).toLocaleDateString()
                          : "N/A"
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Users className="h-4 w-4" />
                      Employee Groups
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {selectedStaff.employeeGroups && selectedStaff.employeeGroups.length > 0 ? (
                        selectedStaff.employeeGroups.map((groupId) => {
                          const groupName = getGroupName(groupId);
                          const style = getGroupStyle(groupName);
                          return (
                            <Badge key={groupId} style={{ backgroundColor: style.bg, color: style.text, border: 'none' }}>
                              {groupName}
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-sm text-gray-500">N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <CheckCircle className="h-4 w-4" />
                      Status
                    </label>
                    <Badge
                      style={
                        selectedStaff.isActive !== false
                          ? { backgroundColor: '#4CAF50', color: 'white', border: 'none' }
                          : { backgroundColor: '#F44336', color: 'white', border: 'none' }
                      }
                    >
                      {selectedStaff.isActive !== false ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default StaffList;